import { createLogger } from '@svs/nova-core/log';
import { BetButton } from '@svs/ui/react/bet-button';
import type { ReactNode } from 'react';

import { type CouponIndex, isOutcome } from '../../schema/coupon/entry.js';
import { cn } from '../utils/entry.js';

const logger = createLogger('poolbetting.coupon');

export type CouponOutcomeBaseProps = {
  buttonClassName?: string;
  className?: string;
  isSelected?: boolean;
  // outcome is optional since it could be over/under with no outcome for X outcome
  outcome?: string;
  index: number;
  isEmptyOutcome?: boolean;
  buttonSize?: React.ComponentProps<typeof BetButton>['size'];
  onButtonChange?: (index: number, couponIndex?: CouponIndex) => void;
  onPointerDown?: () => void;
  onTouchStart?: () => void;
  onPointerCancel?: () => void;
  onTouchCancel?: () => void;
};

export type CouponOutcomeProps = CouponOutcomeBaseProps & {
  eventNumber: number;
  isDisabled?: boolean;
  couponIndex?: CouponIndex;
};

/**
 * This component renders the correct structure for an outcome in a coupon. It handles if the outcome is disabled and if it is selected.
 *
 * @param root0 The couponoutcome input props
 * @param root0.eventNumber Number for event
 * @param root0.index Index for the outcome
 * @param root0.isSelected wether or not the outcome is selected
 * @param root0.outcome Outcome to be displayed
 * @param root0.isDisabled Is the input field is disabled
 * @param root0.onButtonChange Function to be called when the input field is changed
 * @param root0.isEmptyOutcome Boolean for if the outcome is empty
 * @param root0.couponIndex Index of the coupon, used for multi coupon
 * @param root0.buttonSize Size of the ui button
 * @param root0.buttonClassName Classname for the button, used for styling
 * @param root0.onPointerDown Handler for pointer down event
 * @param root0.onTouchStart Handler for touch start event
 * @param root0.onPointerCancel Handler for pointer cancel event
 * @param root0.onTouchCancel Handler for touch cancel event
 * @returns Component to visually represent an outcome and select it if needed
 */
export default function CouponOutcome({
  eventNumber,
  index,
  isSelected,
  outcome,
  isDisabled,
  onButtonChange,
  isEmptyOutcome,
  couponIndex,
  buttonSize,
  buttonClassName,
  onPointerDown,
  onTouchStart,
  onPointerCancel,
  onTouchCancel,
}: CouponOutcomeProps): ReactNode {
  return (
    <BetButton
      className={cn(
        'w-full',
        !isSelected && 'text-primary-variant-fg border-primary-variant-fg border',
        buttonClassName
      )}
      data-testid={`couponButton_${eventNumber}_${index}${couponIndex === undefined ? '' : `_${couponIndex}`}`}
      size={buttonSize}
      disabled={isDisabled ?? isEmptyOutcome}
      value={outcome?.toString() ?? ''}
      onPointerDown={onPointerDown}
      onTouchStart={onTouchStart}
      onPointerCancel={onPointerCancel}
      onTouchCancel={onTouchCancel}
      onStateChange={
        onButtonChange
          ? (): void => {
              if (!isOutcome(index)) {
                logger.warn('Invalid outcome index', { index, eventNumber, couponIndex });
                return;
              }
              onButtonChange(index, couponIndex);
            }
          : undefined
      }
      state={isSelected ? 'checked' : 'unchecked'}
    >
      {outcome}
    </BetButton>
  );
}
